import { Gym, ILeaderboardConfig } from 'fitbeat.models';
import moment from 'moment';
import React, { CSSProperties, useState } from 'react';
import { metricInfo } from '../leaderboardUIHelper';
import LeaderboardMetricInfoModal from './LeaderboardMetricInfoModel';

interface IProps {
  formData: ILeaderboardConfig;
  orderedGyms: Gym[];
  timezones: string[];
  isOrgAdmin: boolean;
  allGyms: boolean;
  isUpdate: boolean;
  hasActivityRestriction: boolean;
  isBroken: boolean;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => void;
  handleSelectChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

const LeaderboardConfigBasicOptions: React.FC<IProps> = ({
  formData,
  orderedGyms,
  timezones,
  isOrgAdmin,
  allGyms,
  isUpdate,
  hasActivityRestriction,
  isBroken,
  handleInputChange,
  handleSelectChange,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleInfoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div style={styles.formColumn}>
      <h3 style={styles.columnHeader} className='leaderboard-create-headers'>
        Basic Options
      </h3>
      <label style={styles.inputLabel}>
        Leaderboard Name:
        <input
          type='text'
          name='name'
          value={formData.name}
          onChange={handleInputChange}
          required
          style={styles.inputField}
          disabled={isBroken}
        />
      </label>
      <label style={styles.inputLabel}>
        Description (optional):
        <input
          type='text'
          name='description'
          value={formData.description}
          onChange={handleInputChange}
          style={styles.inputField}
          disabled={isBroken}
        />
      </label>
      {isOrgAdmin && !allGyms && (
        <label style={styles.inputLabel}>
          Applies to members of Gym:
          <select
            name='gymName'
            value={formData.gymName}
            onChange={handleSelectChange}
            required
            style={styles.inputField}
            disabled={isUpdate || isBroken}
          >
            {orderedGyms.map((gym) => (
              <option key={gym.name} value={gym.name}>
                {gym.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <label style={styles.inputLabel}>
        Frequency:
        <select
          name='frequency'
          value={formData.frequency}
          onChange={handleSelectChange}
          style={styles.inputField}
          disabled={isUpdate || isBroken}
        >
          <option value='one-time'>One-time</option>
          <option value='daily'>Daily</option>
          <option value='weekly'>Weekly</option>
          <option value='monthly'>Monthly</option>
        </select>
      </label>
      <label style={styles.inputLabel}>
        Start Date:
        <input
          type='date'
          name='startDate'
          value={moment(formData.startDate).tz(formData.timezone).format('YYYY-MM-DD')}
          onChange={handleInputChange}
          required
          style={styles.inputField}
          disabled={isUpdate || isBroken}
        />
      </label>
      {formData.frequency === 'one-time' && (
        <label style={styles.inputLabel}>
          End Date:
          <input
            type='date'
            name='endDate'
            value={formData.endDate ? moment(formData.endDate).tz(formData.timezone).format('YYYY-MM-DD') : ''}
            onChange={handleInputChange}
            style={styles.inputField}
          />
        </label>
      )}
      <label style={styles.inputLabel}>
        Competition Type:
        <select
          name='competitionType'
          value={formData.competitionType}
          onChange={handleSelectChange}
          style={styles.inputField}
          disabled={isUpdate || isBroken}
        >
          <option value='individual'>Individual</option>
          <option value='inter-team'>Inter-team</option>
        </select>
      </label>
      <label style={styles.inputLabel}>
        Metric:
        <div style={styles.infoQuestionField}>
          <select
            name='metric'
            value={formData.metric}
            onChange={handleSelectChange}
            style={styles.inputField}
            disabled={isUpdate || isBroken}
          >
            {formData.competitionType === 'individual' && (
              <>
                <option
                  value='total-minutes'
                  title={metricInfo('total-minutes')}
                >
                  Total Minutes
                </option>
                <option
                  value='effort-points'
                  title={metricInfo('effort-points')}
                >
                  Total Effort Points
                </option>
                {!hasActivityRestriction && (
                  <option
                    value='experience-points'
                    title={metricInfo('experience-points')}
                  >
                    Total Experience Points
                  </option>
                )}
              </>
            )}
            {formData.competitionType === 'inter-team' && (
              <>
                <option
                  value='team-weighted-minutes-score'
                  title={metricInfo('team-weighted-minutes-score')}
                >
                  Weighted Team Minutes
                </option>
                <option
                  value='team-weighted-points-score'
                  title={metricInfo('team-weighted-points-score')}
                >
                  Weighted Team Effort Points
                </option>
                {!hasActivityRestriction && (
                  <option
                    value='team-weighted-xp-score'
                    title={metricInfo('team-weighted-xp-score')}
                  >
                    Weighted Team Experience Points
                  </option>
                )}
              </>
            )}
          </select>
          <img
            height={20}
            width={20}
            src='assets/images/question.png'
            style={styles.infoIcon}
            onClick={handleInfoClick}
          />
        </div>
      </label>
      <LeaderboardMetricInfoModal isOpen={isModalOpen} metric={formData.metric} closeModal={closeModal} />
      <label style={styles.inputLabel}>
        Timezone:
        <select
          name='timezone'
          value={formData.timezone}
          onChange={handleInputChange}
          required
          style={styles.inputField}
          disabled={isUpdate || isBroken}
        >
          {timezones.map((timezone) => (
            <option key={timezone} value={timezone}>
              {timezone}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
};

const styles: { [key: string]: CSSProperties } = {
  formColumn: {
    marginRight: '16px',
  },
  columnHeader: {
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 'bold',
    fontSize: '1.2rem',
  },
  inputLabel: {
    display: 'block',
    fontWeight: 'bold',
    marginBottom: '8px',
    flexDirection: 'row',
  },
  inputField: {
    width: '100%',
    padding: '8px',
    marginBottom: '8px',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  infoQuestionField: {
    display: 'flex',
    alignItems: 'center',
  },
  infoIcon: {
    marginBottom: '8px',
    marginLeft: '8px',
  },
};

export default LeaderboardConfigBasicOptions;
